import { Modal, ActionButton, DataTable, Text } from 'components/commons';
import { useApi, useFilter } from 'hooks';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './customer-account-detail-modal.module.scss';
import { formatAmount, formatCurrency } from 'utils';
import { customerClaimsColumns } from './customer-account-detail-modal-columns';
import { customerAccountDetailFilterState } from './customer-account-detail-modal-filter.state';

import { getTransactionProductCustomerAccount } from 'apis/transaction.api';
import moment from 'moment/moment';
import CustomerAccountDetailModalFilter from './customer-account-detail-modal-filter';

const CustomerAccountDetailModal = ({ customerAccountId, close, active }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  const { modifyFilters, requestState, filterState, submitFilter } = useFilter(customerAccountDetailFilterState());

  const {
    request: getCustomerClaimRequest,
    loading: fetchingCustomerClaim,
    result: getCustomerClaimResult = { transactions: [], count: 0 }
  } = useApi({
    api: getTransactionProductCustomerAccount,
    pageError: true,
    params: {
      customerAccountId
    }
  });

  const preparedCustomerClaimsData = useMemo(() => {
    const { transactions } = getCustomerClaimResult;
    if (transactions.length > 0) {
      const preparedData = transactions.map((c) => {
        const map = new Map();
        map.set('month', moment(`${filterState.year}-${c?.month}-01`).format('MMMM YYYY') || '--');
        map.set('amount', formatCurrency(c?.totalAmount || 0));
        map.set('totalNewBalance', formatCurrency(c?.totalNewBalance || 0));

        return map;
      });
      return preparedData;
    }
    return [];
  }, [filterState.year, getCustomerClaimResult]);
  const fetchCustomerClaims = useCallback(
    (rs) => {
      getCustomerClaimRequest(submitFilter({ ...rs }));
    },
    [getCustomerClaimRequest, submitFilter]
  );

  useEffect(() => {
    if (active && !isLoaded) {
      fetchCustomerClaims(requestState);
      setIsLoaded(true);
    }
  }, [active, fetchCustomerClaims, getCustomerClaimRequest, isLoaded, requestState]);

  useEffect(() => {
    if (close && !active) {
      setIsLoaded(false);
    }
  }, [active, close, fetchCustomerClaims]);

  const onChangePageCb = useCallback(
    (page) => {
      const { requestState } = modifyFilters({ page });
      fetchCustomerClaims(requestState);
    },
    [fetchCustomerClaims, modifyFilters]
  );

  const onChangePageSizeCb = useCallback(
    (perPage) => {
      const { requestState } = modifyFilters({ perPage, page: 1 });
      fetchCustomerClaims(requestState);
    },
    [fetchCustomerClaims, modifyFilters]
  );

  const onYearChangeCb = useCallback(
    (year) => {
      const { requestState } = modifyFilters({ year, page: 1 });
      fetchCustomerClaims(requestState);
    },
    [fetchCustomerClaims, modifyFilters]
  );

  return (
    <>
      <Modal active={active} title={'Shares Details'} close={close} className={styles.modal}>
        <div className={styles.filters}>
          <div>
            <CustomerAccountDetailModalFilter filterState={filterState} onYearChange={onYearChangeCb} />
          </div>
        </div>

        <div className={styles.content}>
          <DataTable
            loading={fetchingCustomerClaim}
            columns={customerClaimsColumns}
            data={preparedCustomerClaimsData}
            dataCount={getCustomerClaimResult.count}
            onChangePage={onChangePageCb}
            onChangePageSize={onChangePageSizeCb}
            isPaginationHidden={true}
          />
        </div>

        <br />

        <Text strong>Total Share Months: {formatAmount(getCustomerClaimResult.total?.totalShareMonths || 0)}</Text>
        <Text strong>Average Share Months: {formatAmount(getCustomerClaimResult.total?.averageShareMonths || 0)}</Text>

        <ActionButton
          right
          items={[
            {
              text: 'Close',
              onClick: close
            }
          ]}
        />
      </Modal>
    </>
  );
};

export default CustomerAccountDetailModal;
